import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom';
import { BackButton, CancelButton, SubmitButton } from '../../../../Components/Utils/Buttons';
import Breadcrumb from '../../../../Components/Utils/Breadcrumb';
import { useToast } from '../../../../Components/Others/ToastServices';
import SignatureCanvas from 'react-signature-canvas';
import useAddFormData from '../../../../Components/Hooks/useAddFormData';
import axiosInstance from '../../../../Services/axiosCommon';

function GeneratorForm() {
  const location = useLocation();
  const { id,scheduleViewId } = useParams();
  const toast = useToast();
  
  const pageIndex = location.state?.pageIndex;
  const searchQuery = location.state?.searchQuery;
  const form_data = location?.state?.form;

  const [selectedFile, setSelectedFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  const inspectorSigCanvas = useRef(null);
  const facilityRepSigCanvas = useRef(null);
  const [signatures, setSignatures] = useState({
    inspector: null,
    facilityRep: null
  });
    const [forms, setForms] = useState([]);
  const form = location.state?.form;
  

  const { Oncancel, addFormData, isLoading } = useAddFormData();

  const [formData, setFormData] = useState({
    facility_name: '',
    test_date: '',
    facility_address: '',
    test_type: '',
    inspector_name: '',
    inspector_certification: '',
    manufacturer: '',
    model: '',
    serial_number: '',
    fuel_type: '',
    rated_power: '',
    rated_voltage: '',
    installation_date: '',
    last_maintainance: '',
    
    // Visual Inspection
    general_condition: {
      engine: "",
      alternator: "",
      overall: ""
    },
    leaks_present: false,
    corrosion_present: false,
    mounting_secure: false,
    ventilation_adequate: false,
    
    // Fuel System
    fuel_level: "",
    fuel_quality: "",
    fuel_filter_condition: "",
    fuel_pump_operation: "",
    fuel_lines_secure: false,
    
    // Electrical System
    battery_voltage: "",
    battery_condition: "",
    battery_terminals_clean: false,
    charging_system: "",
    control_panel: "",
    
    // Startup Test
    manual_start: false,
    auto_start: false,
    start_time: "",
    initial_volt: "",
    initial_freq: "",
    oil_pressure: "",
    
    // Load Test
    load_applied: "",
    test_duration: "",
    performance_satis: false,
    loaded_volt: "",
    loaded_freq: "",
    coolant_temp: "",
    
    // Maintenance
    oil_changed: false,
    oil_filter_replaced: false,
    fuel_filter_replaced: false,
    air_filter_replaced: false,
    spark_plugs_replaced: false,
    battery_maintenance: false,
    coolant_system_flushed: false,
    belts_inspected: false,
    belts_replaced: false,
    other_maintainance: "",
    
    // Test Summary
    overall_result: "",
    next_test_due: "",
    next_maintenance_due: "",
    recommendations: "",
    
    // Signatures
    inspector_name: "",
    inspector_date: "",
    facility_representative_name: "",
    facility_representative_signature: "",
    facility_representative_date: "",
    
    // Compliance
    nfpa_110_compliant: true,
    local_code_compliant: true,
    manufacturer_guidelines_followed: true,
    compliance_notes: "",
    
    isSubmited: false
  });

  const [deficiencies, setDeficiencies] = useState([]);

  // Fetch generator data when component mounts
  useEffect(() => {
    const generatorId = id || form_data?.inspectiionId;
    if (generatorId) {
      fetchGeneratorData(generatorId);
    }
  }, [id, form_data]);

  const fetchGeneratorData = async (generatorId) => {
    try {
      setDataLoading(true);
      const response = await axiosInstance.get(`/generator/${generatorId}`);
      if (response.data.status === true && response.data.data) {
        const apiData = response.data.data;
        
        // Update form data with API response
        setFormData(prev => ({
          ...prev,
          ...apiData,
          general_condition: apiData.general_condition || prev.general_condition
        }));
        
        // Set deficiencies if they exist
        if (apiData.deficiencies && Array.isArray(apiData.deficiencies)) {
          setDeficiencies(apiData.deficiencies);
        }
        
        // Load signatures if they exist
        if (apiData.facility_representative_signature) {
          setSignatures(prev => ({ 
            ...prev, 
            facilityRep: apiData.facility_representative_signature 
          }));
          setTimeout(() => {
            if (facilityRepSigCanvas.current && apiData.facility_representative_signature) {
              facilityRepSigCanvas.current.fromDataURL(apiData.facility_representative_signature);
            }
          }, 100);
        }
        
        if (apiData.inspector_signature) {
          setSignatures(prev => ({ 
            ...prev, 
            inspector: apiData.inspector_signature 
          }));
          setTimeout(() => {
            if (inspectorSigCanvas.current && apiData.inspector_signature) {
              inspectorSigCanvas.current.fromDataURL(apiData.inspector_signature);
            }
          }, 100);
        }
        
        setIsEditing(true);
        // toast.open({
        //   type: "success",
        //   message: "Generator data loaded successfully",
        // });
      }
    } catch (error) {
      console.error('Error fetching generator data:', error);
      toast.open({
        type: "error",
        message: "Failed to load generator data",
      });
    } finally {
      setDataLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prevState => ({
      ...prevState,
      [parent]: {
        ...prevState[parent],
        [field]: value
      }
    }));
  };

  const handleDeficiencyChange = (index, field, value) => {
    const updatedDeficiencies = deficiencies.map((def, idx) => 
      idx === index ? { ...def, [field]: value } : def
    );
    setDeficiencies(updatedDeficiencies);
  };

  const addDeficiency = () => {
    setDeficiencies(prev => [...prev, {
      Severity: "",
      "Target Completion Date": "",
      Description: "",
      Recommendation: "",
      "Estimated Cost ($)": ""
    }]);
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A';

    const date = new Date(dateTime);

    const formattedDate = date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\//g, '-');

    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    return `${formattedDate}, ${formattedTime}`;
  };
  
  const removeDeficiency = (index) => {
    setDeficiencies(prev => prev.filter((_, idx) => idx !== index));
  };

  // Signature functions
  useEffect(() => {
    const handleResize = () => {
      if (signatures.facilityRep && facilityRepSigCanvas.current) {
        facilityRepSigCanvas.current.fromDataURL(signatures.facilityRep);
      }
      if (signatures.inspector && inspectorSigCanvas.current) {
        inspectorSigCanvas.current.fromDataURL(signatures.inspector);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [signatures]);

  const clearSignature = (type) => {
    if (type === "inspector" && inspectorSigCanvas.current) {
      inspectorSigCanvas.current.clear();
      setSignatures((prev) => ({ ...prev, inspector: null }));
      toast.open({
        type: "success",
        message: "Inspector signature cleared",
      });
    } else if (type === "facilityRep" && facilityRepSigCanvas.current) {
      facilityRepSigCanvas.current.clear();
      setSignatures((prev) => ({ ...prev, facilityRep: null }));
      toast.open({
        type: "success",
        message: "Facility Representative signature cleared",
      });
    }
  };

  const saveSignature = (type) => {
    try {
      if (type === "inspector" && inspectorSigCanvas.current) {
        const dataURL = inspectorSigCanvas.current.toDataURL("image/png");
        setSignatures((prev) => ({ ...prev, inspector: dataURL }));
        toast.open({
          type: "success",
          message: "Inspector signature saved",
        });
        return dataURL;
      } else if (type === "facilityRep" && facilityRepSigCanvas.current) {
        const dataURL = facilityRepSigCanvas.current.toDataURL("image/png");
        setSignatures((prev) => ({ ...prev, facilityRep: dataURL }));
        toast.open({
          type: "success",
          message: "Facility Representative signature saved",
        });
        return dataURL;
      }
      return null;
    } catch (error) {
      console.error("Error saving signature:", error);
      toast.error("Failed to save signature");
      return null;
    }
  };

  const handleCanvasClick = (type) => {
    if ((type === "inspector" && signatures.inspector) || (type === "facilityRep" && signatures.facilityRep)) {
      toast.open({
        type: "warning",
        message: `Please clear the existing ${type} signature before adding a new one`,
      });
      return false;
    }
    return true;
  };

  const prepareSubmissionData = (isSubmit = false) => {
    const submissionData = {
      ...formData,
      general_condition: formData.general_condition || {
        engine: "Good",
        alternator: "Good", 
        overall: "Good"
      },
      deficiencies: deficiencies.filter(def => def.Description || def.Recommendation),
      facility_representative_signature: signatures.facilityRep || "",
      inspector_signature: signatures.inspector || "",
      isSubmited: isSubmit,
      
      // Convert numeric fields to proper types
      rated_power: formData.rated_power ? parseInt(formData.rated_power) : 0,
      rated_voltage: formData.rated_voltage ? parseInt(formData.rated_voltage) : 0,
      fuel_level: formData.fuel_level ? parseInt(formData.fuel_level) : 0,
      battery_voltage: formData.battery_voltage ? parseFloat(formData.battery_voltage) : 0,
      start_time: formData.start_time ? parseInt(formData.start_time) : 0,
      initial_volt: formData.initial_volt ? parseInt(formData.initial_volt) : 0,
      initial_freq: formData.initial_freq ? parseInt(formData.initial_freq) : 0,
      oil_pressure: formData.oil_pressure ? parseInt(formData.oil_pressure) : 0,
      load_applied: formData.load_applied ? parseInt(formData.load_applied) : 0,
      test_duration: formData.test_duration ? parseInt(formData.test_duration) : 0,
      loaded_volt: formData.loaded_volt ? parseInt(formData.loaded_volt) : 0,
      loaded_freq: formData.loaded_freq ? parseInt(formData.loaded_freq) : 0,
      coolant_temp: formData.coolant_temp ? parseInt(formData.coolant_temp) : 0,
    };

    // Remove empty strings and convert to null for better API handling
    Object.keys(submissionData).forEach(key => {
      if (submissionData[key] === "") {
        submissionData[key] = null;
      }
    });

    return submissionData;
  };

  const dataURLToBlob = async (dataURL) => {
    const response = await fetch(dataURL);
    return await response.blob();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFile(files);
  };

  const SaveFormData = async () => {
    try {
      setIsSaving(true);
      
      const generatorId = id || form_data?.inspectiionId;
      if (!generatorId) {
        toast.error("Invalid generator ID");
        return;
      }

      const formDataObj = new FormData();
      const draftData = prepareSubmissionData(false);
      
      console.log('Saving draft data:', draftData);
      formDataObj.append("json_data", JSON.stringify(draftData));

      // Add signatures if they exist
      if (signatures.inspector && signatures.inspector !== "") {
        const inspectorBlob = await dataURLToBlob(signatures.inspector);
        formDataObj.append("inspectorSignature", inspectorBlob, 'inspector_signature.png');
      }
      
      if (signatures.facilityRep && signatures.facilityRep !== "") {
        const facilityRepBlob = await dataURLToBlob(signatures.facilityRep);
        formDataObj.append("facilityRepSignature", facilityRepBlob, 'facilityRep_signature.png');
      }

      // Add attachments
      if (selectedFile && selectedFile.length > 0) {
        selectedFile.forEach((file) => {
          formDataObj.append("attachments", file);
        });
      }

      const response = await axiosInstance.post(
        `/generator/submit/${generatorId}`,
        formDataObj,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data?.status === true) {
        toast.open({
          type: "success",
          message: "Generator form saved as draft successfully",
        });
      } else {
        toast.error(response.data?.msg || "Failed to save form");
      }

    } catch (err) {
      console.error("Error saving form:", err);
      toast.error("Failed to save form");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const generatorId = id || form_data?.inspectiionId;
      if (!generatorId) {
        toast.error("Invalid generator ID");
        return;
      }

      const formDataObj = new FormData();
      const submissionData = prepareSubmissionData(true);
      
      console.log('Submitting data:', submissionData);
      formDataObj.append("json_data", JSON.stringify(submissionData));

      if (signatures.inspector && signatures.inspector !== "") {
        const inspectorBlob = await dataURLToBlob(signatures.inspector);
        formDataObj.append("inspectorSignature", inspectorBlob, 'inspector_signature.png');
      }
      
      if (signatures.facilityRep && signatures.facilityRep !== "") {
        const facilityRepBlob = await dataURLToBlob(signatures.facilityRep);
        formDataObj.append("facilityRepSignature", facilityRepBlob, 'facilityRep_signature.png');
      }

      if (selectedFile && selectedFile.length > 0) {
        selectedFile.forEach((file) => {
          formDataObj.append("attachments", file);
        });
      }

      await addFormData({
        url: `/generator/submit/${generatorId}`,
        formData: formDataObj,
        navigateUrl: `/forms/insforms`,
        resetForm: () => {
          // Reset form to initial state
          setFormData({
            facility_name: '',
            test_date: '',
            facility_address: '',
            test_type: '',
            inspector_name: '',
            inspector_certification: '',
            manufacturer: '',
            model: '',
            serial_number: '',
            fuel_type: '',
            rated_power: '',
            rated_voltage: '',
            installation_date: '',
            last_maintainance: '',
            general_condition: { engine: "", alternator: "", overall: "" },
            leaks_present: false,
            corrosion_present: false,
            mounting_secure: false,
            ventilation_adequate: false,
            fuel_level: "",
            fuel_quality: "",
            fuel_filter_condition: "",
            fuel_pump_operation: "",
            fuel_lines_secure: false,
            battery_voltage: "",
            battery_condition: "",
            battery_terminals_clean: false,
            charging_system: "",
            control_panel: "",
            manual_start: false,
            auto_start: false,
            start_time: "",
            initial_volt: "",
            initial_freq: "",
            oil_pressure: "",
            load_applied: "",
            test_duration: "",
            performance_satis: false,
            loaded_volt: "",
            loaded_freq: "",
            coolant_temp: "",
            oil_changed: false,
            oil_filter_replaced: false,
            fuel_filter_replaced: false,
            air_filter_replaced: false,
            spark_plugs_replaced: false,
            battery_maintenance: false,
            coolant_system_flushed: false,
            belts_inspected: false,
            belts_replaced: false,
            other_maintainance: "",
            overall_result: "",
            next_test_due: "",
            next_maintenance_due: "",
            recommendations: "",
            inspector_name: "",
            inspector_date: "",
            facility_representative_name: "",
            facility_representative_signature: "",
            facility_representative_date: "",
            nfpa_110_compliant: true,
            local_code_compliant: true,
            manufacturer_guidelines_followed: true,
            compliance_notes: "",
            isSubmited: false
          });
          setDeficiencies([]);
          setSignatures({ inspector: null, facilityRep: null });
          setSelectedFile(null);
          inspectorSigCanvas.current?.clear();
          facilityRepSigCanvas.current?.clear();
        },
      });

    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  if (dataLoading) {
    return (
      <div className="w-full h-full py-2 px-4">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading generator data...</div>
        </div>
      </div>
    );
  }

  const currentForm = forms.find(item => item.id === parseInt(scheduleViewId)) || form;


  return (
    <div className="w-full h-full py-2 px-4 ">
      <div className="flex justify-between ">
        <Breadcrumb
          items={[
            {
              name: "Schedule",
              link: `/inspection/schedule/newform/${form_data?.inspectiionId}`,
            },
            { name: "Generator" },
          ]}
        />
        <div className=" flex gap-2">
          <Link
            to={`/inspection/schedule/newform/${form_data?.inspectiionId}`}
            state={{
              currentPage: pageIndex,
              searchQuery: searchQuery != "" ? searchQuery : "",
            }}
          >
            <BackButton link={true} />
          </Link>
        </div>
      </div>
         {currentForm && (
              <div className="h-auto  rounded-lg p-2">
                <div className="md:flex gap-4 w-full">
  <div className="flex gap-3">
    <div className="flex items-start">
      <div className=" flex-shrink-0 font-medium text-xs">Schedule :</div>
      <div className="text-[#606279] text-xs flex-1 mx-1">
        {formatDateTime(currentForm.dateTime)}
      </div>
    </div>
    
    <div className="flex items-start">
      <div className=" flex-shrink-0 font-medium text-xs">Assigned To :</div>
      <div className="text-[#606279] font-medium text-xs flex-1 mx-1">
        {currentForm.assignees && currentForm.assignees.length > 0 
          ? currentForm.assignees.map(a => a.assigneeName).join(", ") 
          : 'Unassigned'
        }
      </div>
    </div>
    
    <div className="flex items-start">
      <div className=" flex-shrink-0 font-medium text-xs">Updated by :</div>
      <div className="text-[#606279] text-xs flex-1 mx-1">
        {currentForm.updatedByName || 'None'}
      </div>
    </div>

    <div className="flex items-start">
      <div className=" flex-shrink-0 font-medium text-xs">Last Updated :</div>
      <div className="text-[#606279] font-medium text-xs flex-1 mx-1">
        {currentForm.updatedOn ? formatDateTime(currentForm.updatedOn) : 'Never'}
      </div>
    </div>
  </div>
  
</div>

              </div>
        
            )}

      <div className="bg-white mx-auto  h-[calc(100vh-158px)] w-full rounded-md flex flex-col overflow-y-auto p-4">
     
        <div className="bg-white rounded-lg p-6 mb-4 shadow-sm border border-gray-200">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {'Emergency Power System Test (NFPA 110)'}
          </h1>
          <p className="text-gray-600">Comprehensive generator testing and maintenance documentation</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information Section */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Basic Information</h2>
            <p className="text-gray-600 mb-4">Facility and inspector information for the emergency power test</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Facility Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="facility_name"
                  value={formData.facility_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  placeholder="Enter facility name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Test Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="test_date"
                  value={formData.test_date}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Facility Address
                </label>
                <textarea
                  name="facility_address"
                  value={formData.facility_address}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  placeholder="Enter complete facility address"
                  rows={3}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Inspector Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="inspector_name"
                  value={formData.inspector_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  placeholder="Enter inspector name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Test Type
                </label>
                <select
                  name="test_type"
                  value={formData.test_type}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                >
                  <option value="Select">Select</option>

                  <option value="Monthly">Monthly Test</option>
                  <option value="Quarterly">Quarterly Test</option>
                  <option value="Annual">Annual Test</option>
                  <option value="Emergency">Emergency Test</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Inspector Certification
                </label>
                <input
                  type="text"
                  name="inspector_certification"
                  value={formData.inspector_certification}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  placeholder="Enter certification details"
                />
              </div>
            </div>

            <div className=" rounded-xl  p-6 border  mt-5">
              <h2 className="text-lg font-semibold text-gray-800">
                NFPA 110 Maintenance Schedule Reference
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Weekly Tasks</h3>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Run generator in auto mode check</li>
                    <li>Verify fuel/oil/coolant levels</li>
                    <li>Check for alarms and faults</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Monthly Tasks</h3>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Exercise generator under load (per OEM)</li>
                    <li>Inspect battery and charging system</li>
                    <li>Inspect air intake and exhaust</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Generator Information Section */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Generator Information</h2>
            <p className="text-gray-600 mb-4">Technical specifications and details of the emergency generator</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manufacturer <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="manufacturer"
                  value={formData.manufacturer}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  placeholder="e.g., Caterpillar, Cummins, Kohler"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  placeholder="Enter model"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Serial Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="serial_number"
                  value={formData.serial_number}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  placeholder="Enter serial number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fuel Type
                </label>
                <select
                  name="fuel_type"
                  value={formData.fuel_type}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                >
                  <option value="Select">Select</option>
                  <option value="Diesel">Diesel</option>
                  <option value="Gasoline">Gasoline</option>
                  <option value="Propane">Propane</option>
                  <option value="Natural Gas">Natural Gas</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rated Power (kW) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="rated_power"
                  value={formData.rated_power}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  placeholder="Enter rated power in kW"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rated Voltage (V)
                </label>
                <input
                  type="number"
                  name="rated_voltage"
                  value={formData.rated_voltage}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                  placeholder="Enter rated voltage"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Installation Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="installation_date"
                  value={formData.installation_date}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Maintenance Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="last_maintainance"
                  value={formData.last_maintainance}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                />
              </div>
            </div>
          </div>

          {/* Visual Inspection Section */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Visual Inspection</h2>
            <p className="text-gray-600">General condition and visual assessment</p>

            <div className="mt-6">
              <h3 className="font-semibold text-gray-700 mb-3">General Condition</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Engine Condition</label>
                  <select
                    value={formData.general_condition.engine}
                    onChange={(e) => handleNestedChange('general_condition', 'engine', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                  <option value="Select">Select</option>
                    <option value="Excellent">Excellent</option>
                    <option value="Good">Good</option>
                    <option value="Fair">Fair</option>
                    <option value="Poor">Poor</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Alternator Condition</label>
                  <select
                    value={formData.general_condition.alternator}
                    onChange={(e) => handleNestedChange('general_condition', 'alternator', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                  <option value="Select">Select</option>
                    <option value="Excellent">Excellent</option>
                    <option value="Good">Good</option>
                    <option value="Fair">Fair</option>
                    <option value="Poor">Poor</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Overall Condition</label>
                  <select
                    value={formData.general_condition.overall}
                    onChange={(e) => handleNestedChange('general_condition', 'overall', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                  <option value="Select">Select</option>
                    <option value="Excellent">Excellent</option>
                    <option value="Good">Good</option>
                    <option value="Fair">Fair</option>
                    <option value="Poor">Poor</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="leaks_present"
                    checked={formData.leaks_present}
                    onChange={handleChange}
                  />
                  <span>Leaks Present</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="corrosion_present"
                    checked={formData.corrosion_present}
                    onChange={handleChange}
                  />
                  <span>Corrosion Present</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="mounting_secure"
                    checked={formData.mounting_secure}
                    onChange={handleChange}
                  />
                  <span>Mounting Secure</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="ventilation_adequate"
                    checked={formData.ventilation_adequate}
                    onChange={handleChange}
                  />
                  <span>Ventilation Adequate</span>
                </label>
              </div>
            </div>
          </div>

          {/* Fuel System Section */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Fuel System</h2>
            <p className="text-gray-600">Fuel level, quality, and system components</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fuel Level (%)
                </label>
                <input
                  type="number"
                  name="fuel_level"
                  value={formData.fuel_level}
                  onChange={handleChange}
                  placeholder="Enter fuel level"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fuel Quality
                </label>
                <select
                  name="fuel_quality"
                  value={formData.fuel_quality}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="Select">Select</option>

                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                  <option value="Poor">Poor</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fuel Filter Condition
                </label>
                <select
                  name="fuel_filter_condition"
                  value={formData.fuel_filter_condition}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="Select">Select</option>
                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                  <option value="Poor">Poor</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fuel Pump Operation
                </label>
                <select
                  name="fuel_pump_operation"
                  value={formData.fuel_pump_operation}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="Select">Select</option>
                  <option value="Normal">Normal</option>
                  <option value="Irregular">Irregular</option>
                  <option value="Failing">Failing</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="fuel_lines_secure"
                  checked={formData.fuel_lines_secure}
                  onChange={handleChange}
                />
                <span>Fuel Lines Secure</span>
              </div>
            </div>
          </div>

          {/* Electrical System Section */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Electrical System</h2>
            <p className="text-gray-600">Battery status, wiring, and component conditions</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Battery Voltage (V)
                </label>
                <input
                  type="number"
                  name="battery_voltage"
                  value={formData.battery_voltage}
                  onChange={handleChange}
                  placeholder="Enter battery voltage"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Battery Condition
                </label>
                <select
                  name="battery_condition"
                  value={formData.battery_condition}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="Select">Select</option>
                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                  <option value="Poor">Poor</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Charging System
                </label>
                <select
                  name="charging_system"
                  value={formData.charging_system}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="Select">Select</option>
                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                  <option value="Poor">Poor</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Control Panel
                </label>
                <select
                  name="control_panel"
                  value={formData.control_panel}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="Select">Select</option>
                  <option value="Normal">Normal</option>
                  <option value="Irregular">Irregular</option>
                  <option value="Failing">Failing</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="battery_terminals_clean"
                  checked={formData.battery_terminals_clean}
                  onChange={handleChange}
                />
                <span>Battery Terminals Clean</span>
              </div>
            </div>
          </div>

          {/* Startup Test Section */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Startup Test</h2>
            <p className="text-gray-600">Manual and automatic start test results</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="manual_start"
                  checked={formData.manual_start}
                  onChange={handleChange}
                />
                <span>Manual Start Successful</span>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="auto_start"
                  checked={formData.auto_start}
                  onChange={handleChange}
                />
                <span>Automatic Start Successful</span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Startup Time (seconds)
                </label>
                <input
                  type="number"
                  name="start_time"
                  value={formData.start_time}
                  onChange={handleChange}
                  placeholder="Enter startup time"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Initial Voltage (V)
                </label>
                <input
                  type="number"
                  name="initial_volt"
                  value={formData.initial_volt}
                  onChange={handleChange}
                  placeholder="Enter initial voltage"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Initial Frequency (Hz)
                </label>
                <input
                  type="number"
                  name="initial_freq"
                  value={formData.initial_freq}
                  onChange={handleChange}
                  placeholder="Enter initial frequency"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Oil Pressure (PSI)
                </label>
                <input
                  type="number"
                  name="oil_pressure"
                  value={formData.oil_pressure}
                  onChange={handleChange}
                  placeholder="Enter oil pressure"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>
          </div>

          {/* Load Test Section */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Load Test</h2>
            <p className="text-gray-600">Generator performance under load conditions</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Load Applied (%)
                </label>
                <input
                  type="number"
                  name="load_applied"
                  value={formData.load_applied}
                  onChange={handleChange}
                  placeholder="Enter load applied"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Test Duration (minutes)
                </label>
                <input
                  type="number"
                  name="test_duration"
                  value={formData.test_duration}
                  onChange={handleChange}
                  placeholder="Enter test duration"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Loaded Voltage (V)
                </label>
                <input
                  type="number"
                  name="loaded_volt"
                  value={formData.loaded_volt}
                  onChange={handleChange}
                  placeholder="Enter loaded voltage"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Loaded Frequency (Hz)
                </label>
                <input
                  type="number"
                  name="loaded_freq"
                  value={formData.loaded_freq}
                  onChange={handleChange}
                  placeholder="Enter loaded frequency"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Coolant Temp (°F)
                </label>
                <input
                  type="number"
                  name="coolant_temp"
                  value={formData.coolant_temp}
                  onChange={handleChange}
                  placeholder="Enter coolant temperature"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="performance_satis"
                  checked={formData.performance_satis}
                  onChange={handleChange}
                />
                <span>Performance Satisfactory</span>
              </div>
            </div>
          </div>

          {/* Maintenance Performed Section */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Maintenance Performed</h2>
            <p className="text-gray-600">Record all maintenance activities performed during this inspection</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              {[
                "oil_changed",
                "oil_filter_replaced", 
                "fuel_filter_replaced",
                "air_filter_replaced",
                "spark_plugs_replaced",
                "battery_maintenance",
                "coolant_system_flushed",
                "belts_inspected",
                "belts_replaced",
              ].map((item) => (
                <label key={item} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name={item}
                    checked={formData[item] || false}
                    onChange={handleChange}
                  />
                  <span>{item.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</span>
                </label>
              ))}
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Other Maintenance Performed
              </label>
              <textarea
                name="other_maintainance"
                value={formData.other_maintainance || ""}
                onChange={handleChange}
                placeholder="Describe any additional maintenance activities..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>
          </div>

          {/* Deficiencies Section */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Deficiencies and Recommendations
                </h2>
                <p className="text-sm text-gray-500">
                  Document any issues found and recommended corrective actions
                </p>
              </div>
              <button 
                type="button"
                onClick={addDeficiency}
                className="px-4 py-2 text-sm font-medium text-red-600 border border-red-600 rounded-lg hover:bg-red-600 hover:text-white transition"
              >
                Add Deficiency
              </button>
            </div>

            {deficiencies.map((deficiency, index) => (
              <div key={index} className="border rounded-lg p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Severity
                    </label>
                    <select
                      value={deficiency.Severity}
                      onChange={(e) => handleDeficiencyChange(index, 'Severity', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                  <option value="Select">Select</option>

                      <option value="Minor">Minor</option>
                      <option value="Major">Major</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Target Completion Date
                    </label>
                    <input
                      type="date"
                      value={deficiency["Target Completion Date"]}
                      onChange={(e) => handleDeficiencyChange(index, 'Target Completion Date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={deficiency.Description}
                      onChange={(e) => handleDeficiencyChange(index, 'Description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Enter deficiency description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Recommendation
                    </label>
                    <input
                      type="text"
                      value={deficiency.Recommendation}
                      onChange={(e) => handleDeficiencyChange(index, 'Recommendation', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Enter recommendation"
                    />
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <div className="w-48">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estimated Cost ($)
                    </label>
                    <input
                      type="number"
                      value={deficiency["Estimated Cost ($)"]}
                      onChange={(e) => handleDeficiencyChange(index, 'Estimated Cost ($)', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Enter estimated cost"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeDeficiency(index)}
                    className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Test Summary Section */}
          <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Test Summary</h2>
              <p className="text-sm text-gray-500">Overall test results and conclusions</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Overall Result
                </label>
                <select
                  name="overall_result"
                  value={formData.overall_result}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                >
                  <option value="Select">Select</option>
                  <option value="Pass">Pass</option>
                  <option value="Fail">Fail</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Next Test Due
                </label>
                <input
                  type="date"
                  name="next_test_due"
                  value={formData.next_test_due}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Next Maintenance Due
                </label>
                <input
                  type="date"
                  name="next_maintenance_due"
                  value={formData.next_maintenance_due}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Recommendations
              </label>
              <textarea
                name="recommendations"
                value={formData.recommendations}
                onChange={handleChange}
                rows="3"
                placeholder="Enter any additional recommendations..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              />
            </div>

            <hr />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                  <div>
                    <label className="block text-sm text-gray-700">Date</label>
                    <input
                      type="date"
                      name="inspector_date"
                      value={formData.inspector_date}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                </div>

              <div>
                  <div>
                    <label className="block text-sm text-gray-700">Representative Name</label>
                    <input
                      type="text"
                      name="facility_representative_name"
                      value={formData.facility_representative_name}
                      onChange={handleChange}
                      placeholder="Enter facility representative name"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                 
              </div>
              <div>
                 <div>
                    <label className="block text-sm text-gray-700">Date</label>
                    <input
                      type="date"
                      name="facility_representative_date"
                      value={formData.facility_representative_date}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    />
                  </div>
              </div>
            </div>

            {/* Signature Canvas */}
            {/* <div className="w-full p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Signature</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="relative border border-gray-300 rounded-lg p-4 bg-gray-50">
                  <div 
                    className="w-full h-40 bg-white border border-gray-400 rounded-md overflow-hidden"
                    onClick={() => handleCanvasClick("facilityRep")}
                  >
                    <SignatureCanvas
                      penColor="black"
                      canvasProps={{
                        width: 500,
                        height: 160,
                        className: "w-full h-full",
                        onMouseDown: () => !handleCanvasClick("facilityRep") && null,
                        onTouchStart: () => !handleCanvasClick("facilityRep") && null,
                      }}
                      ref={facilityRepSigCanvas}
                    />
                  </div>
                  <div className="flex justify-between items-center mt-3">
                    <span className="text-xs font-semibold text-gray-500">
                      Facility Representative
                    </span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => clearSignature("facilityRep")}
                        className="text-xs text-red-600 hover:bg-red-100 px-3 py-1 rounded-md transition"
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (signatures.facilityRep) {
                            toast.open({
                              type: "warning",
                              message: "Signature already saved. Please clear first to add a new one.",
                            });
                            return;
                          }
                          saveSignature("facilityRep");
                        }}
                        className="text-xs bg-baseColor-primary text-white px-3 py-1 rounded-md hover:bg-baseColor-primary transition disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={signatures.facilityRep}
                      >
                        {signatures.facilityRep ? "Saved" : "Save"}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="relative border border-gray-300 rounded-lg p-4 bg-gray-50">
                  <div 
                    className="w-full h-40 bg-white border border-gray-400 rounded-md overflow-hidden"
                    onClick={() => handleCanvasClick("inspector")}
                  >
                    <SignatureCanvas
                      penColor="black"
                      canvasProps={{
                        width: 500,
                        height: 160,
                        className: "w-full h-full",
                        onMouseDown: () => !handleCanvasClick("inspector") && null,
                        onTouchStart: () => !handleCanvasClick("inspector") && null,
                      }}
                      ref={inspectorSigCanvas}
                    />
                  </div>
                  <div className="flex justify-between items-center mt-3">
                    <span className="text-xs font-semibold text-gray-500">
                      Inspector
                    </span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => clearSignature("inspector")}
                        className="text-xs text-red-600 hover:bg-red-100 px-3 py-1 rounded-md transition"
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (signatures.inspector) {
                            toast.open({
                              type: "warning",
                              message: "Signature already saved. Please clear first to add a new one.",
                            });
                            return;
                          }
                          saveSignature("inspector");
                        }}
                        className="text-xs bg-baseColor-primary text-white px-3 py-1 rounded-md hover:bg-baseColor-primary transition disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={signatures.inspector}
                      >
                        {signatures.inspector ? "Saved" : "Save"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div> */}
          </div>

          {/* Compliance Section */}
          <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Compliance</h2>
              <p className="text-sm text-gray-500">Verify compliance with standards and guidelines</p>
            </div>
            
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="nfpa_110_compliant"
                  checked={formData.nfpa_110_compliant}
                  onChange={handleChange}
                  className="h-4 w-4 text-red-600"
                />
                NFPA 110 Compliant
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="local_code_compliant"
                  checked={formData.local_code_compliant}
                  onChange={handleChange}
                  className="h-4 w-4 text-red-600"
                />
                Local Code Compliant
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="manufacturer_guidelines_followed"
                  checked={formData.manufacturer_guidelines_followed}
                  onChange={handleChange}
                  className="h-4 w-4 text-red-600"
                />
                Manufacturer Guidelines Followed
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Compliance Notes
              </label>
              <textarea
                name="compliance_notes"
                value={formData.compliance_notes}
                onChange={handleChange}
                placeholder="Enter any compliance-related notes or exceptions..."
                className="w-full border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-red-400"
                rows="4"
              />
            </div>
          </div>

         

          {/* Submit Buttons */}
          <div className="flex justify-end gap-4 mt-6">
            <CancelButton
              name={isSaving ? "Saving..." : "Save"}
              handleClick={SaveFormData}
              disabled={isSaving || isLoading}
            />
            <SubmitButton 
                 isLoading={isLoading} 
               />
          </div>
        </form>
      </div>
    </div>
  );
}

export default GeneratorForm;